// =====================================================
// COMPONENTE: BypassCerticamara (Botón de Pánico)
// =====================================================
// ⭐ VERSIÓN POR MÓDULOS: dos botones independientes,
// uno para MÉDICO y otro para FARMACIA. Cada uno con su
// propio vidrio protector, estado, modales e historial.
// Solo accesible por superadmin.
// =====================================================
import React, { useState, useEffect } from 'react';
import { Alert, Spinner, Badge, Modal, Button, Form } from 'react-bootstrap';

const API_BASE = process.env.REACT_APP_NODE_ENV === 'production'
  ? process.env.REACT_APP_API_URL_PRODUCTION
  : 'http://localhost:3001';

// ⭐ Los módulos que se pueden controlar por separado
const MODULOS = [
  { id: 'medico',   label: 'Documentos Médicos',   icono: '🏥' },
  { id: 'farmacia', label: 'Documentos Farmacia',  icono: '💊' },
];

const BypassCerticamara = ({ onSuccess }) => {
  // =====================================================
  // ESTADO GENERAL
  // =====================================================
  // estadoModulos guarda el estado de AMBOS módulos: { medico: {...}, farmacia: {...} }
  const [estadoModulos, setEstadoModulos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [procesando, setProcesando] = useState(false);

  // Cuál módulo tiene el vidrio roto en este momento (null = ninguno)
  const [vidrioRotoModulo, setVidrioRotoModulo] = useState(null);

  // Modal activar — guardamos a qué módulo aplica
  const [modalActivar, setModalActivar] = useState({ show: false, modulo: null });
  const [motivo, setMotivo] = useState('');

  // Modal desactivar — guardamos a qué módulo aplica
  const [modalDesactivar, setModalDesactivar] = useState({ show: false, modulo: null });
  const [observacion, setObservacion] = useState('');

  // Historial (es compartido, incluye ambos módulos)
  const [showHistorial, setShowHistorial] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // =====================================================
  // CARGAR ESTADO INICIAL
  // =====================================================
  useEffect(() => {
    cargarEstado();
    const interval = setInterval(cargarEstado, 30000);
    return () => clearInterval(interval);
  }, []);

  const cargarEstado = async () => {
    try {
      setError(null);
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/estado`, {
        headers: { 'usuario-correo': correo || '' }
      });
      const data = await res.json();
      if (data.success) setEstadoModulos(data.data.modulos);
      else setError(data.error || 'Error al consultar estado');
    } catch (err) {
      console.error('Error:', err);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // ROMPER VIDRIO (por módulo)
  // =====================================================
  const handleRomperVidrio = (modulo) => {
    setVidrioRotoModulo(modulo);
    setTimeout(() => {
      setModalActivar({ show: true, modulo });
    }, 600);
  };

  const cerrarModalActivar = () => {
    setModalActivar({ show: false, modulo: null });
    setMotivo('');
    setTimeout(() => setVidrioRotoModulo(null), 300);
  };

  // =====================================================
  // ACTIVAR BYPASS (de un módulo)
  // =====================================================
  const handleActivar = async () => {
    if (motivo.trim().length < 5) {
      setError('El motivo debe tener al menos 5 caracteres');
      return;
    }
    const modulo = modalActivar.modulo;
    try {
      setProcesando(true);
      setError(null);
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/activar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'usuario-correo': correo || '' },
        body: JSON.stringify({ modulo, motivo: motivo.trim() })
      });
      const data = await res.json();
      if (data.success) {
        onSuccess?.(`🚨 Bypass de ${modulo} activado correctamente`);
        cerrarModalActivar();
        cargarEstado();
      } else {
        setError(data.error || 'Error al activar');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setProcesando(false);
    }
  };

  // =====================================================
  // DESACTIVAR BYPASS (de un módulo)
  // =====================================================
  const handleDesactivar = async () => {
    if (observacion.trim().length < 5) {
      setError('La observación debe tener al menos 5 caracteres');
      return;
    }
    const modulo = modalDesactivar.modulo;
    try {
      setProcesando(true);
      setError(null);
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/desactivar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'usuario-correo': correo || '' },
        body: JSON.stringify({ modulo, observacion: observacion.trim() })
      });
      const data = await res.json();
      if (data.success) {
        onSuccess?.(`✅ Bypass de ${modulo} desactivado. Duración: ${data.data.duracionMinutos}min · Docs: ${data.data.documentosProcesados}`);
        setModalDesactivar({ show: false, modulo: null });
        setObservacion('');
        cargarEstado();
      } else {
        setError(data.error || 'Error al desactivar');
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setProcesando(false);
    }
  };

  // =====================================================
  // HISTORIAL
  // =====================================================
  const cargarHistorial = async () => {
    setLoadingHistorial(true);
    try {
      const correo = localStorage.getItem('correo');
      const res = await fetch(`${API_BASE}/api/bypass/historial`, {
        headers: { 'usuario-correo': correo || '' }
      });
      const data = await res.json();
      if (data.success) setHistorial(data.data.historial);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoadingHistorial(false);
    }
  };

  useEffect(() => {
    if (showHistorial) cargarHistorial();
  }, [showHistorial]);

  // =====================================================
  // FORMATEAR TIEMPO
  // =====================================================
  const formatearTiempo = (minutos) => {
    if (minutos < 60) return `${minutos} min`;
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    return `${h}h ${m}min`;
  };

  // =====================================================
  // RENDER LOADING
  // =====================================================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <Spinner animation="border" variant="danger" />
        <span className="ms-3">Cargando estado del bypass...</span>
      </div>
    );
  }

  // =====================================================
  // SUB-COMPONENTE: TARJETA DE UN MÓDULO
  // =====================================================
  // Renderiza el botón de pánico de UN módulo (médico o farmacia).
  // Reutiliza toda la lógica visual original (vidrio + botón pulsante).
  const TarjetaModulo = ({ modulo }) => {
    const est = estadoModulos?.[modulo.id] || {};
    const activo = est.activo === true;
    const vidrioRoto = vidrioRotoModulo === modulo.id;

    return (
      <div className={`panic-card ${activo ? 'panic-card-active' : 'panic-card-inactive'} p-4`}>
        {/* Título del módulo */}
        <div className="text-center mb-3">
          <h5 className="fw-bold mb-0">
            <span style={{ fontSize: '1.4rem' }}>{modulo.icono}</span> {modulo.label}
          </h5>
        </div>

        {/* Estado */}
        <div className="text-center mb-3">
          {activo ? (
            <>
              <div className="panic-warning-blink mb-2">
                <Badge bg="danger" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                  🚨 BYPASS ACTIVO 🚨
                </Badge>
              </div>
              <p className="text-danger fw-bold mb-0" style={{ fontSize: '0.95rem' }}>
                Sin firma digital Certicámara
              </p>
            </>
          ) : (
            <>
              <Badge bg="success" style={{ fontSize: '0.9rem', padding: '8px 16px' }}>
                ✅ OPERACIÓN NORMAL
              </Badge>
              <p className="text-success fw-bold mt-2 mb-0" style={{ fontSize: '0.95rem' }}>
                Certicámara activa
              </p>
            </>
          )}
        </div>

        {/* BOTÓN GIGANTE con vidrio */}
        <div className="d-flex justify-content-center mb-3">
          <div style={{ position: 'relative', width: 220, height: 220 }}>

            {/* VIDRIO PROTECTOR (solo cuando NO está activo) */}
            {!activo && (
              <div
                className={`glass-protector ${vidrioRoto ? 'glass-broken' : ''}`}
                style={{
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  borderRadius: '50%',
                  zIndex: 10,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => handleRomperVidrio(modulo.id)}
              >
                <div className="text-center" style={{ pointerEvents: 'none' }}>
                  <div style={{ fontSize: '2rem' }}>🔨</div>
                  <div style={{ fontWeight: 700, color: '#dc3545', marginTop: 4, fontSize: '0.9rem' }}>
                    ROMPER VIDRIO
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                    Solo en emergencia
                  </div>
                </div>
              </div>
            )}

            {/* BOTÓN PULSANTE */}
            <button
              onClick={() => activo ? setModalDesactivar({ show: true, modulo: modulo.id }) : null}
              disabled={!activo && !vidrioRoto}
              className={activo ? 'panic-button-pulse-red' : 'panic-button-pulse-green'}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '7px solid',
                borderColor: activo ? '#7f1d1d' : '#14532d',
                background: activo
                  ? 'radial-gradient(circle at 30% 30%, #ef4444, #b91c1c 70%, #7f1d1d)'
                  : 'radial-gradient(circle at 30% 30%, #22c55e, #15803d 70%, #14532d)',
                color: 'white',
                fontWeight: 800,
                fontSize: '1.1rem',
                cursor: activo ? 'pointer' : 'default',
                position: 'relative',
                boxShadow: 'inset -8px -8px 20px rgba(0,0,0,0.3), inset 8px 8px 20px rgba(255,255,255,0.2)',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: 6 }}>
                {activo ? '🚨' : '🟢'}
              </div>
              <div>{activo ? 'DESACTIVAR' : 'PÁNICO'}</div>
              <div style={{ fontSize: '0.65rem', fontWeight: 400, marginTop: 4, opacity: 0.85 }}>
                {activo ? 'Volver a normal' : 'Click para activar'}
              </div>
            </button>
          </div>
        </div>

        {/* DATOS DE LA SESIÓN ACTIVA */}
        {activo && (
          <div className="mt-3 p-3 bg-white rounded-3 border border-danger">
            <div className="row g-2" style={{ fontSize: '0.85rem' }}>
              <div className="col-12">
                <small className="text-muted d-block">Activado por:</small>
                <strong>{est.activadoPor?.nombres || est.activadoPor?.correo}</strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Tiempo activo:</small>
                <strong className="text-danger">⏱ {formatearTiempo(est.minutosActivo)}</strong>
              </div>
              <div className="col-6">
                <small className="text-muted d-block">Docs sin firma:</small>
                <strong className="text-warning">📄 {est.documentosProcesadosSesion}</strong>
              </div>
              <div className="col-12">
                <small className="text-muted d-block">Motivo:</small>
                <em style={{ fontSize: '0.8rem' }}>"{est.motivoActivacion}"</em>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div className="bypass-container">
      {/* ESTILOS (idénticos al original) */}
      <style>{`
        @keyframes pulseRed {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7); }
          50% { box-shadow: 0 0 0 30px rgba(220, 53, 69, 0); }
        }
        @keyframes pulseGreen {
          0%, 100% { box-shadow: 0 0 0 0 rgba(25, 135, 84, 0.5); }
          50% { box-shadow: 0 0 0 20px rgba(25, 135, 84, 0); }
        }
        @keyframes breakGlass {
          0% { opacity: 1; transform: scale(1); filter: blur(0); }
          50% { opacity: 0.7; transform: scale(1.05); filter: blur(1px); }
          100% { opacity: 0; transform: scale(1.2); filter: blur(8px); }
        }
        @keyframes warningBlink {
          0%, 50%, 100% { opacity: 1; }
          25%, 75% { opacity: 0.5; }
        }
        .panic-button-pulse-red { animation: pulseRed 1.5s infinite; }
        .panic-button-pulse-green { animation: pulseGreen 2.5s infinite; }
        .panic-warning-blink { animation: warningBlink 1.5s infinite; }
        .glass-protector {
          background: linear-gradient(135deg,
            rgba(255,255,255,0.4) 0%,
            rgba(200,225,255,0.3) 50%,
            rgba(255,255,255,0.4) 100%);
          backdrop-filter: blur(2px);
          border: 3px solid rgba(255,255,255,0.7);
          box-shadow:
            inset 0 0 20px rgba(255,255,255,0.5),
            0 0 30px rgba(0,0,0,0.1);
          transition: all 0.3s;
        }
        .glass-protector::before {
          content: '';
          position: absolute;
          top: 10%;
          left: 5%;
          width: 30%;
          height: 30%;
          background: linear-gradient(135deg, rgba(255,255,255,0.8), transparent);
          border-radius: 50%;
          filter: blur(8px);
          pointer-events: none;
        }
        .glass-broken { animation: breakGlass 0.6s forwards; }
        .panic-card {
          border-radius: 16px;
          overflow: hidden;
          transition: all 0.3s;
          height: 100%;
        }
        .panic-card-active {
          background: linear-gradient(135deg, #fef2f2, #fee2e2);
          border: 2px solid #dc3545;
        }
        .panic-card-inactive {
          background: linear-gradient(135deg, #f0fdf4, #d1fae5);
          border: 2px solid #198754;
        }
      `}</style>

      {/* Header */}
      <div className="mb-4">
        <h5 className="mb-1">
          <i className="bi bi-exclamation-octagon-fill text-danger me-2"></i>
          Botón de Pánico — Bypass de Certicámara por Módulo
        </h5>
        <small className="text-muted">
          Activa el bypass SOLO del módulo afectado cuando Certicámara esté fallando.
          Los documentos de ese módulo no recibirán firma digital mientras esté activo.
          Los demás módulos siguen funcionando con normalidad.
        </small>
      </div>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* DOS TARJETAS LADO A LADO                            */}
      {/* ═══════════════════════════════════════════════════ */}
      <div className="row g-4 mb-4">
        {MODULOS.map(modulo => (
          <div className="col-md-6" key={modulo.id}>
            <TarjetaModulo modulo={modulo} />
          </div>
        ))}
      </div>

      {/* BOTÓN VER HISTORIAL */}
      <div className="text-center">
        <Button variant="outline-secondary" size="sm" onClick={() => setShowHistorial(true)}>
          <i className="bi bi-clock-history me-2"></i>
          Ver historial de activaciones (todos los módulos)
        </Button>
      </div>

      {/* ═══════════════════════════════════════════════════ */}
      {/* MODAL: ACTIVAR BYPASS                               */}
      {/* ═══════════════════════════════════════════════════ */}
      <Modal show={modalActivar.show} onHide={cerrarModalActivar} centered backdrop="static">
        <Modal.Header closeButton className="bg-danger text-white">
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Activar bypass — {MODULOS.find(m => m.id === modalActivar.modulo)?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>⚠️ Estás a punto de:</strong>
            <ul className="mb-0 mt-2 small">
              <li>Desactivar la firma digital de Certicámara <strong>solo para {MODULOS.find(m => m.id === modalActivar.modulo)?.label}</strong></li>
              <li>Permitir que esos documentos se firmen SOLO con BIOFIRMAS</li>
              <li>Quedar registrado como responsable de esta acción</li>
            </ul>
          </Alert>
          <Form.Group>
            <Form.Label className="fw-bold">
              Motivo de activación: <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: Certicámara no responde para el flujo de farmacia desde las 10am..."
              maxLength={500}
              autoFocus
            />
            <Form.Text className="text-muted">
              Mínimo 5 caracteres · {motivo.length}/500
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModalActivar} disabled={procesando}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleActivar} disabled={procesando || motivo.trim().length < 5}>
            {procesando ? (<><Spinner size="sm" className="me-2" />Activando...</>) : (<>🚨 ACTIVAR BYPASS</>)}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════ */}
      {/* MODAL: DESACTIVAR BYPASS                            */}
      {/* ═══════════════════════════════════════════════════ */}
      <Modal show={modalDesactivar.show} onHide={() => setModalDesactivar({ show: false, modulo: null })} centered backdrop="static">
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="bi bi-check-circle-fill me-2"></i>
            Desactivar bypass — {MODULOS.find(m => m.id === modalDesactivar.modulo)?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="success">
            <strong>✅ Estás reactivando la firma digital con Certicámara para este módulo.</strong>
            <p className="mb-0 mt-2 small">
              Los siguientes documentos de {MODULOS.find(m => m.id === modalDesactivar.modulo)?.label} volverán a procesarse con firma digital normal.
            </p>
          </Alert>
          {modalDesactivar.modulo && estadoModulos?.[modalDesactivar.modulo] && (
            <div className="mb-3 p-3 bg-light rounded">
              <small className="text-muted d-block">Resumen de esta sesión:</small>
              <div>⏱ Estuvo activo durante: <strong>{formatearTiempo(estadoModulos[modalDesactivar.modulo].minutosActivo)}</strong></div>
              <div>📄 Documentos sin firma digital: <strong>{estadoModulos[modalDesactivar.modulo].documentosProcesadosSesion}</strong></div>
            </div>
          )}
          <Form.Group>
            <Form.Label className="fw-bold">
              Observación de cierre: <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Ej: Certicámara confirmó que su incidente fue resuelto a las 4pm..."
              maxLength={500}
              autoFocus
            />
            <Form.Text className="text-muted">
              Mínimo 5 caracteres · {observacion.length}/500
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalDesactivar({ show: false, modulo: null })} disabled={procesando}>
            Cancelar
          </Button>
          <Button variant="success" onClick={handleDesactivar} disabled={procesando || observacion.trim().length < 5}>
            {procesando ? (<><Spinner size="sm" className="me-2" />Desactivando...</>) : (<>✅ DESACTIVAR BYPASS</>)}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ═══════════════════════════════════════════════════ */}
      {/* MODAL: HISTORIAL                                    */}
      {/* ═══════════════════════════════════════════════════ */}
      <Modal show={showHistorial} onHide={() => setShowHistorial(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-clock-history me-2"></i>
            Historial de activaciones del bypass
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingHistorial ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : historial.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
              <p className="mt-2 mb-0">Aún no hay registros de activaciones</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Acción</th>
                    <th>Fecha</th>
                    <th>Usuario</th>
                    <th>Motivo / Observación</th>
                    <th className="text-center">Duración</th>
                    <th className="text-center">Docs</th>
                  </tr>
                </thead>
                <tbody>
                  {historial.map((h, i) => (
                    <tr key={i}>
                      <td>
                        <Badge bg={h.accion === 'activado' ? 'danger' : 'success'}>
                          {h.accion === 'activado' ? '🚨 Activado' : '✅ Desactivado'}
                        </Badge>
                      </td>
                      <td className="small">{new Date(h.fecha).toLocaleString('es-CO')}</td>
                      <td className="small">
                        <strong>{h.realizadoPor?.nombres || '—'}</strong><br />
                        <span className="text-muted">{h.realizadoPor?.correo}</span>
                      </td>
                      <td className="small">{h.motivo}</td>
                      <td className="text-center small">
                        {h.duracionMinutos > 0 ? formatearTiempo(h.duracionMinutos) : '—'}
                      </td>
                      <td className="text-center small">
                        {h.documentosProcesados > 0 ? h.documentosProcesados : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHistorial(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BypassCerticamara;